import type {
  Product, SiteMeta, Order, Facets, Subscriber, AdminStats, AdminAccount,
} from '../types';
import type {
  FinanceSummary, FinanceSummaryResponse, BurnReport, OpsSettings,
  ThriftStore, OperatingExpense, PurchaseReceiptWithItems, PurchaseItem,
  AgingReport, GrowthScenario, InventoryCostRow,
} from '../types/finance';
import { withDemo } from './demoMode';

/* Response supersets the ops endpoints actually return. The base shapes live in
   src/types/finance.ts; these add the extra roll-ups each route computes. */

export interface InventoryBurnResponse {
  burn: BurnReport;
  demo: boolean;
  generated_at: string;
  currency: string;
  settings: OpsSettings;
  window: BurnReport['window'];
  horizon_days: number;
  available_units: number;
  units_sold: number;
  sell_rate_per_day: number | null;
  sell_through_rate: number | null;
  days_of_stock: number | null;
  days_of_stock_label: string;
  stockout_on: string | null;
  replacement_units_per_week: number | null;
  replacement_spend_per_week_cents: number | null;
  replacement_spend_per_horizon_cents: number | null;
  avg_unit_cost_cents: number;
  avg_unit_sale_price_cents: number | null;
  inventory_cost_cents: number;
  inventory_retail_cents: number;
  opex_cents: number;
  rates: BurnRateWindow[];
  growth: { horizon_days: number; scenarios: (GrowthScenario & { units_needed: number })[] }[];
  aging: AgingReport;
  aging_totals: { units: number; retail_cents: number; cost_cents: number };
  dead_stock_units: number;
  dead_stock_value_cents: number;
  items: InventoryCostRow[];
  item_count: number;
  warnings: string[];
}

export interface BurnRateWindow {
  window_days: number;
  label: string;
  units_sold: number;
  sell_rate_per_day: number | null;
  sell_through_rate: number | null;
  days_of_stock: number | null;
  days_of_stock_label: string;
  replacement_units_per_week: number | null;
  replacement_spend_per_week_cents: number | null;
  stockout_on: string | null;
}

export interface ThriftStoreWithStats extends ThriftStore {
  receipt_count: number;
  items_bought: number;
  spend_cents: number;
  avg_receipt_cents: number | null;
  avg_unit_cost_cents: number | null;
  last_purchase_at: string | null;
}

export interface ThriftStoresListResponse {
  stores: ThriftStoreWithStats[];
  totals: {
    stores: number; active: number; receipts: number;
    items: number; spend_cents: number; avg_unit_cost_cents: number | null;
  };
  demo: boolean;
}

export interface PurchaseReceiptRow extends PurchaseReceiptWithItems {
  items: PurchaseItem[];
  store: ThriftStore | null;
  store_name: string | null;
  line_count: number;
  item_count: number;
  items_total_cents: number;
  linked_product_count: number;
}

export interface PurchasesListResponse {
  receipts: PurchaseReceiptRow[];
  page: { limit: number; offset: number; total: number; has_more: boolean };
  totals: { receipt_count: number; subtotal_cents: number; tax_cents: number; total_cents: number };
  demo: boolean;
}

export interface PurchaseDetailResponse { receipt: PurchaseReceiptRow; demo: boolean }

export interface PurchaseMutationResponse {
  ok: true; id: string; receipt: PurchaseReceiptRow;
  costed_products: number; warning?: string; warnings?: string[];
}

export interface ExpensesListResponse {
  expenses: OperatingExpense[];
  totals: { total_cents: number; count: number; by_category: { category: string; amount_cents: number; count: number }[] };
  filters: Record<string, unknown>;
  categories: string[];
  demo: boolean;
}

export interface OpsSettingsFullResponse {
  settings: OpsSettings;
  defaults: OpsSettings;
  stored_keys: string[];
  updated_at: string | null;
  demo: boolean;
}

export interface PurchaseQuery {
  store_id?: string; from?: string; to?: string; limit?: number; offset?: number;
}
export interface ExpenseQuery {
  category?: string; from?: string; to?: string; limit?: number;
}

export type { FinanceSummary, FinanceSummaryResponse, BurnReport, OpsSettings, ThriftStore, OperatingExpense };

/* ── Anthropic spend ledger + prompt library ─────────────────────────────── */

export interface AiUsageTotals {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  total_tokens: number;
  cost_nanos: number;
  cost_usd: number;
  /** Calls on a model missing from the rate table — their cost is NOT in cost_usd. */
  unpriced_calls: number;
}

export interface AiUsageSlice {
  label: string;
  calls: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  unpriced_calls: number;
}

export interface AiUsageCall {
  id: string;
  created_at: string;
  endpoint: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  image_count: number | null;
  priced: boolean;
  cost_usd: number;
}

export interface AiUsageResponse {
  ok: true;
  /** False until migration 0009 is applied — totals are zeros, not an error. */
  ready: boolean;
  configured: boolean;
  currency: string;
  timezone: string;
  as_of: string;
  migration?: string;
  totals: { today: AiUsageTotals; month: AiUsageTotals; all: AiUsageTotals };
  by_model: AiUsageSlice[];
  by_endpoint: AiUsageSlice[];
  recent: AiUsageCall[];
}

export interface AiPrompt {
  key: string;
  label: string;
  description: string;
  used_by: string;
  role: 'system' | 'user';
  placeholders: Array<{ token: string; meaning: string }>;
  value: string;
  default_value: string;
  is_default: boolean;
  is_overridden: boolean;
  updated_at: string | null;
  updated_by: string | null;
  /** How many saves deep the undo stack is for this prompt. */
  history_depth: number;
}

export interface AiPromptsResponse {
  ok: true;
  ready: boolean;
  migration?: string;
  prompts: AiPrompt[];
}

/**
 * Fired after any call that spends Anthropic tokens, so the live tally can
 * refresh immediately instead of waiting for its next poll.
 */
export const AI_USAGE_CHANGED_EVENT = 'ai-usage:changed';
const announceAiSpend = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(AI_USAGE_CHANGED_EVENT));
};

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers || {}) },
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      if (data && typeof data === 'object' && 'error' in data) msg = String((data as { error: string }).error);
    } catch {/* ignore */}
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface ProductFilters {
  category?: string;
  gender?: string;
  brand?: string;
  size?: string;
  q?: string;
  featured?: number;
}

export interface CheckoutBody {
  product_ids: string[];
  customer_email: string;
  customer_name: string;
  customer_phone?: string;
  delivery_address: { line1?: string; line2?: string; city?: string; region?: string; postal?: string };
  delivery_instructions?: string;
  payment_method: 'etransfer' | 'card';
  notes?: string;
}

export interface CheckoutResult {
  order_number: string;
  status: string;
  payment_method: 'etransfer' | 'card';
  total_cents: number;
  checkout_url?: string;
  dev_mode?: boolean;
  etransfer?: { email: string; name: string; amount_cents: number };
}

export interface SubscribeBody {
  email?: string;
  phone?: string;
  name?: string;
  gender?: string;
  sizes?: string[];
  brands?: string[];
  categories?: string[];
  source?: string;
}

export interface AiAnalyzeResult {
  ok: boolean;
  configured: boolean;
  error?: string;
  image_count?: number;
  fields?: {
    title?: string; brand?: string; model?: string; colorway?: string;
    release_year?: string; style_code?: string;
    product_type?: string; category?: string;
    gender?: string; size?: string; size_label?: string; size_source?: string;
    color?: string; secondary_color?: string; material?: string; pattern?: string;
    style?: string; condition?: string; seasonality?: string; retail_category?: string;
    description?: string; tags?: string[]; est_retail_cad?: number; est_resale_cad?: number;
    confidence?: Record<string, number>; notes?: string;
    /** What the model saw before naming a brand — the audit trail behind `brand`. */
    visual_evidence?: string[];
  };
  pricing?: { price?: number; compareAt?: number };
}

export const api = {
  meta: () => http<SiteMeta>('/api/public/meta'),
  facets: () => http<Facets>('/api/public/facets'),
  products: (params: ProductFilters = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') q.set(k, String(v));
    }
    return http<{ products: Product[] }>(`/api/public/products?${q.toString()}`);
  },
  product: (slug: string) =>
    http<{ product: Product }>(`/api/public/product?slug=${encodeURIComponent(slug)}`),
  checkout: (body: CheckoutBody) =>
    http<CheckoutResult>('/api/public/checkout', { method: 'POST', body: JSON.stringify(body) }),
  devConfirm: (orderNumber: string) =>
    http<{ ok: true }>('/api/public/dev-confirm', {
      method: 'POST', body: JSON.stringify({ order_number: orderNumber }),
    }),
  order: (orderNumber: string) =>
    http<{ order: Order }>(`/api/public/order?order=${encodeURIComponent(orderNumber)}`),
  subscribe: (body: SubscribeBody) =>
    http<{ ok: true }>('/api/public/subscribe', { method: 'POST', body: JSON.stringify(body) }),

  // ── Admin ──────────────────────────────────────────────────────────────
  adminSession: () => http<{ authenticated: boolean; username: string | null }>('/api/admin/session'),
  /** Sign in with password + second factor (6-digit TOTP or a recovery code). */
  adminLogin: (username: string, password: string, code: string) =>
    http<{ ok: true; username: string; usedRecoveryCode?: boolean; recoveryCodesLeft?: number }>(
      '/api/admin/login', { method: 'POST', body: JSON.stringify({ username, password, code }) }),
  adminSetupStatus: () => http<{ needs_setup: boolean; admins: number }>('/api/admin/setup'),
  adminSetupStart: (body: { username?: string; password: string; display_name?: string; invite_token?: string }) =>
    http<{ ok: true; username: string; secret: string; otpauth: string; confirm_token: string }>(
      '/api/admin/setup', { method: 'POST', body: JSON.stringify({ action: 'start', ...body }) }),
  adminSetupConfirm: (confirm_token: string, code: string) =>
    http<{ ok: true; username: string; recovery_codes: string[] }>(
      '/api/admin/setup', { method: 'POST', body: JSON.stringify({ action: 'confirm', confirm_token, code }) }),
  adminList: () => http<{ self: string | null; admins: AdminAccount[] }>('/api/admin/admins'),
  adminInvite: (username: string, display_name?: string) =>
    http<{ ok: true; username: string; display_name: string | null; invite_token: string }>(
      '/api/admin/admins', { method: 'POST', body: JSON.stringify({ username, display_name }) }),
  adminRemove: (id: string) =>
    http<{ ok: true }>(`/api/admin/admins?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminLogout: () => http<{ ok: true }>('/api/admin/logout', { method: 'POST' }),
  adminStats: () => http<AdminStats>('/api/admin/stats'),
  adminProducts: () => http<{ products: Product[] }>('/api/admin/products'),
  adminProductCreate: (body: unknown) =>
    http<{ id: string; sku: string; slug: string }>('/api/admin/products', {
      method: 'POST', body: JSON.stringify(body),
    }),
  /** Multipart ingest (product + images in one call) — same path the iOS app uses. */
  adminProductIngest: async (form: FormData) => {
    const res = await fetch('/api/admin/products', { method: 'POST', body: form, credentials: 'same-origin' });
    if (!res.ok) {
      let msg = `Upload failed (${res.status})`;
      try { const d = await res.json(); if (d && typeof d === 'object' && 'error' in d) msg = String((d as { error: string }).error); } catch {/* ignore */}
      throw new Error(msg);
    }
    return (await res.json()) as { ok: boolean; id: string; sku: string; slug: string; product_url: string; images: string[]; updated: boolean };
  },
  /** AI photo intake: analyze 1–10 photos of ONE item → suggested fields + confidence. */
  adminAnalyzeItem: async (blobs: Blob[]): Promise<AiAnalyzeResult> => {
    const fd = new FormData();
    blobs.forEach((b, i) => fd.append('images', b, `shot-${i + 1}.jpg`));
    const res = await fetch('/api/admin/analyze', { method: 'POST', body: fd, credentials: 'same-origin' });
    // The request reached the server, so the ledger may have a new row — nudge
    // the tally now rather than making the owner wait for the next poll.
    announceAiSpend();
    let data: unknown = null;
    try { data = await res.json(); } catch {/* ignore */}
    if (res.status === 503 && data && typeof data === 'object' && (data as AiAnalyzeResult).configured === false) {
      return data as AiAnalyzeResult; // not configured — surface a friendly message, don't throw
    }
    if (!res.ok) {
      const msg = data && typeof data === 'object' && 'error' in data
        ? String((data as { error: string }).error) : `Analysis failed (${res.status})`;
      throw new Error(msg);
    }
    return data as AiAnalyzeResult;
  },
  /** AI photo grouping: send N photos of mixed items → which indices belong together. */
  adminGroupPhotos: async (blobs: Blob[]): Promise<{ ok: boolean; configured?: boolean; error?: string; groups: Array<{ indices: number[]; label: string }>; unmatched: number[] }> => {
    const fd = new FormData();
    blobs.forEach((b, i) => fd.append('images', b, `photo-${i + 1}.jpg`));
    const res = await fetch('/api/admin/group-photos', { method: 'POST', body: fd, credentials: 'same-origin' });
    announceAiSpend();
    let data: unknown = null;
    try { data = await res.json(); } catch {/* ignore */}
    if (res.status === 503 && data && typeof data === 'object' && (data as { configured?: boolean }).configured === false) {
      return { ok: false, configured: false, groups: [], unmatched: [], error: (data as { error?: string }).error || 'AI not configured' };
    }
    if (!res.ok) {
      const msg = data && typeof data === 'object' && 'error' in data
        ? String((data as { error: string }).error)
        : res.status === 503
          ? 'Photo grouping was terminated by the server or is temporarily unavailable. Retry the import.'
          : `Grouping failed (${res.status})`;
      throw new Error(msg);
    }
    return data as { ok: boolean; groups: Array<{ indices: number[]; label: string }>; unmatched: number[] };
  },
  /* ── Anthropic spend ledger ────────────────────────────────────────── */
  /** Live token/dollar tally. Cheap enough to poll; safe to call anywhere in admin. */
  adminAiUsage: () => http<AiUsageResponse>('/api/admin/ai-usage'),

  /* ── Prompt library ────────────────────────────────────────────────── */
  adminAiPrompts: () => http<AiPromptsResponse>('/api/admin/ai-prompts'),
  adminAiPromptSave: (key: string, value: string) =>
    http<{ ok: true; key: string; value: string; is_default?: boolean; unchanged?: boolean }>(
      '/api/admin/ai-prompts',
      { method: 'PUT', body: JSON.stringify({ key, value }) },
    ),
  /** Undo the last save — repeatable, walking back to the shipped default. */
  adminAiPromptRevert: (key: string) =>
    http<{ ok: true; key: string; value: string; is_default?: boolean; exhausted?: boolean }>(
      '/api/admin/ai-prompts',
      { method: 'POST', body: JSON.stringify({ key, action: 'revert' }) },
    ),
  /** Discard every override and go straight back to the text we ship. */
  adminAiPromptReset: (key: string) =>
    http<{ ok: true; key: string; value: string; is_default?: boolean; unchanged?: boolean }>(
      '/api/admin/ai-prompts',
      { method: 'POST', body: JSON.stringify({ key, action: 'reset' }) },
    ),

  adminProduct: (id: string) =>
    http<{ product: Product }>(`/api/admin/product?id=${encodeURIComponent(id)}`),
  adminProductUpdate: (id: string, body: unknown) =>
    http<{ ok: true }>(`/api/admin/product?id=${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  adminProductDelete: (id: string) =>
    http<{ ok: true }>(`/api/admin/product?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminImageUpload: async (productId: string, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    const res = await fetch(`/api/admin/images?product_id=${encodeURIComponent(productId)}`, {
      method: 'POST', body: fd, credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    return (await res.json()) as { created: string[] };
  },
  adminImageRegisterUrl: (productId: string, url: string, alt?: string) =>
    http<{ created: string[] }>(`/api/admin/images?product_id=${encodeURIComponent(productId)}`, {
      method: 'POST', body: JSON.stringify({ url, alt }),
    }),
  adminImageUpdate: (id: string, body: { is_primary?: boolean; sort_order?: number; alt?: string }) =>
    http<{ ok: true }>(`/api/admin/images?id=${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  adminImageDelete: (id: string) =>
    http<{ ok: true }>(`/api/admin/images?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminOrders: () => http<{ orders: Order[] }>('/api/admin/orders'),
  adminOrderAction: (id: string, action: 'paid' | 'cancel' | 'delivered' | 'out_for_delivery' | 'scheduled') =>
    http<{ ok: true }>(`/api/admin/orders?id=${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify({ action }),
    }),
  adminSubscribers: () => http<{ subscribers: Subscriber[] }>('/api/admin/subscribers'),

  // ── Product image backgrounds (PhotoRoom) ────────────────────────────────
  adminBackgroundsStatus: () =>
    http<{ configured: boolean; r2: boolean; total: number; processed: number; unprocessed: number; default_bg_color: string }>(
      '/api/admin/reprocess-images'),
  adminReprocessBackgrounds: (body: { bg_color?: string; scope?: 'all' | 'unprocessed'; limit?: number; cursor?: string }) =>
    http<{ ok: boolean; processed: number; failed: string[]; next_cursor: string; remaining: number; total: number }>(
      '/api/admin/reprocess-images', { method: 'POST', body: JSON.stringify(body) }),

  /* ── Ops: financials, burn rate, thrift hauls ──────────────────────────
     Every read threads the demo flag through withDemo(). Writes never carry
     it — the server refuses mutations while demo=1, and the UI disables the
     controls anyway, so a demo session cannot reach a write path at all. */

  adminFinanceSummary: (windowDays = 30, demo = false, seriesDays = 90) =>
    http<FinanceSummaryResponse>(withDemo(
      `/api/admin/finance-summary?window=${windowDays}&series_days=${seriesDays}`, demo)),

  adminInventoryBurn: (windowDays = 30, demo = false, horizonDays = 30) =>
    http<InventoryBurnResponse>(withDemo(
      `/api/admin/inventory-burn?window=${windowDays}&horizon=${horizonDays}`, demo)),

  adminPurchases: (params: PurchaseQuery = {}, demo = false) =>
    http<PurchasesListResponse>(withDemo(`/api/admin/purchases${queryOf(params)}`, demo)),

  adminPurchase: (id: string, demo = false) =>
    http<PurchaseDetailResponse>(withDemo(
      `/api/admin/purchases?id=${encodeURIComponent(id)}`, demo)),

  /** Accepts a plain object (JSON) or FormData (multipart, for a receipt photo). */
  adminPurchaseSave: async (payload: unknown, id?: string): Promise<PurchaseMutationResponse> => {
    const path = id ? `/api/admin/purchases?id=${encodeURIComponent(id)}` : '/api/admin/purchases';
    const method = id ? 'PUT' : 'POST';
    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      // Deliberately no content-type header: the browser must set the
      // multipart boundary itself, and http() would override it.
      const res = await fetch(path, { method, body: payload, credentials: 'same-origin' });
      if (!res.ok) {
        let msg = `Upload failed (${res.status})`;
        try {
          const data = await res.json();
          if (data && typeof data === 'object' && 'error' in data) msg = String((data as { error: string }).error);
        } catch {/* ignore */}
        throw new Error(msg);
      }
      return (await res.json()) as PurchaseMutationResponse;
    }
    return http<PurchaseMutationResponse>(path, { method, body: JSON.stringify(payload) });
  },

  adminPurchaseDelete: (id: string) =>
    http<{ ok: true }>(`/api/admin/purchases?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),

  adminThriftStores: (demo = false, activeOnly = false) =>
    http<ThriftStoresListResponse>(withDemo(
      `/api/admin/thrift-stores${activeOnly ? '?active=1' : ''}`, demo)),

  adminThriftStoreSave: (body: Partial<ThriftStore>, id?: string) =>
    http<{ ok: true; id: string; store: ThriftStore }>(
      id ? `/api/admin/thrift-stores?id=${encodeURIComponent(id)}` : '/api/admin/thrift-stores',
      { method: id ? 'PUT' : 'POST', body: JSON.stringify(body) }),

  adminThriftStoreDelete: (id: string) =>
    http<{ ok: true }>(`/api/admin/thrift-stores?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),

  adminExpenses: (params: ExpenseQuery = {}, demo = false) =>
    http<ExpensesListResponse>(withDemo(`/api/admin/expenses${queryOf(params)}`, demo)),

  adminExpenseSave: (body: Partial<OperatingExpense>, id?: string) =>
    http<{ ok: true; id: string; expense: OperatingExpense }>(
      id ? `/api/admin/expenses?id=${encodeURIComponent(id)}` : '/api/admin/expenses',
      { method: id ? 'PUT' : 'POST', body: JSON.stringify(body) }),

  adminExpenseDelete: (id: string) =>
    http<{ ok: true }>(`/api/admin/expenses?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),

  adminOpsSettings: (demo = false) =>
    http<OpsSettingsFullResponse>(withDemo('/api/admin/ops-settings', demo)),

  adminOpsSettingsUpdate: (partial: Partial<OpsSettings>) =>
    http<{ ok: true; settings: OpsSettings; updated: string[] }>(
      '/api/admin/ops-settings', { method: 'PUT', body: JSON.stringify(partial) }),
};

/** Build a query string from defined params only, so `?a=&b=` never happens. */
function queryOf(params: object): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export function formatCents(cents: number, currency = 'CAD'): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}
